import { useState, useEffect } from "react";
import { MessageCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { WHATSAPP_GROUP_LINK } from "@/lib/links";

const navLinks = [
  { label: "Lista", href: "/lista" },
  { label: "Eventos", href: "#eventos" },
  { label: "Sobre", href: "#sobre" },
  { label: "Ambiente", href: "#ambiente" },
  { label: "Regras da Casa", href: "/regras" },
  { label: "Contato", href: "#contato" },
];

let navbarScrolledState = false;

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const next = window.scrollY > 30;
      if (next === navbarScrolledState) return;
      navbarScrolledState = next;
      setScrolled(next);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setMobileOpen(false);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [mobileOpen]);

  const scrollTo = (href) => {
    setMobileOpen(false);
    if (href.startsWith("/")) {
      navigate(href);
      return;
    }
    if (location.pathname !== "/") {
      navigate("/");
      window.setTimeout(() => {
        const el = document.querySelector(href);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 80);
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const goHome = () => {
    setMobileOpen(false);
    if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
    }
  };

  return (
    <>
      <nav
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-out ${
          scrolled
            ? "navbar-scrolled py-3"
            : "bg-transparent py-5"
        }`}
        style={{ willChange: 'background, padding, box-shadow' }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between">
          {/* Logo */}
          <button
            type="button"
            data-testid="navbar-logo"
            onClick={goHome}
            className="flex h-12 items-center cursor-pointer transition duration-500 hover:opacity-85 md:h-14"
            aria-label="Secrets Club"
          >
            <img
              src="/logo%20(1).png"
              alt="Secrets Club"
              className="h-full w-auto object-contain"
            />
          </button>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-10">
          {navLinks.map((link) => (
            <button
              type="button"
              key={link.href}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
              onClick={() => scrollTo(link.href)}
              className="text-sm font-['Manrope'] font-light tracking-[0.15em] uppercase text-white/60 hover:text-white transition-all duration-500 relative group cursor-pointer"
            >
              {link.label}
              <span className="absolute -bottom-1 left-0 w-0 h-[1px] bg-[#CC0000] group-hover:w-full transition-all duration-500" />
            </button>
          ))}
          <a
            href={WHATSAPP_GROUP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 rounded-full bg-[#128C4A] px-5 py-2 text-xs font-['Manrope'] uppercase tracking-[0.16em] text-white shadow-[0_0_24px_rgba(18,140,74,0.24)] transition-all duration-500 hover:bg-[#17A85A] hover:shadow-[0_0_32px_rgba(18,140,74,0.42)]"
            data-testid="nav-whatsapp-group"
          >
            <MessageCircle size={13} />
            Grupo VIP
          </a>
        </div>

          {/* Mobile Hamburger */}
          <button
            type="button"
            data-testid="mobile-menu-btn"
            aria-label={mobileOpen ? "Fechar menu" : "Abrir menu"}
            aria-expanded={mobileOpen}
            className={`md:hidden flex flex-col gap-[5px] cursor-pointer z-[60] ${
              mobileOpen ? "hamburger-open" : ""
            }`}
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <span className="hamburger-line" />
            <span className="hamburger-line" />
            <span className="hamburger-line" />
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[45] flex h-[100dvh] flex-col items-center justify-center gap-8 overflow-y-auto bg-[#050505] px-6 py-28 md:hidden"
            data-testid="mobile-menu"
          >
            {navLinks.map((link, i) => (
              <motion.button
                type="button"
                key={link.href}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                onClick={() => scrollTo(link.href)}
                className="text-2xl font-['Cormorant_Garamond'] text-white/80 hover:text-white tracking-wider cursor-pointer"
                data-testid={`mobile-nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </motion.button>
            ))}
            <motion.a
              href={WHATSAPP_GROUP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.38 }}
              onClick={() => setMobileOpen(false)}
              className="flex items-center gap-2 rounded-full bg-[#128C4A] px-6 py-3 text-sm font-['Manrope'] uppercase tracking-[0.16em] text-white"
              data-testid="mobile-whatsapp-group"
            >
              <MessageCircle size={16} />
              Entrar no Grupo VIP
            </motion.a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
